import { useEffect, useState } from "react";
import { X, Play } from "lucide-react";

function toEmbedUrl(url) {
    if (!url) return null;
    try {
        const u = new URL(url);
        // YouTube
        if (u.hostname.includes("youtube.com")) {
            const id = u.searchParams.get("v");
            if (id) return { type: "iframe", src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
            // /embed/<id> already
            const parts = u.pathname.split("/").filter(Boolean);
            const embedIdx = parts.indexOf("embed");
            if (embedIdx >= 0 && parts[embedIdx + 1]) {
                return { type: "iframe", src: `https://www.youtube.com/embed/${parts[embedIdx + 1]}?autoplay=1&rel=0` };
            }
        }
        if (u.hostname.includes("youtu.be")) {
            const id = u.pathname.replace("/", "");
            return { type: "iframe", src: `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` };
        }
        // Vimeo
        if (u.hostname.includes("vimeo.com")) {
            const id = u.pathname.split("/").filter(Boolean).pop();
            return { type: "iframe", src: `https://player.vimeo.com/video/${id}?autoplay=1` };
        }
        // Direct video file
        if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(u.pathname)) {
            return { type: "video", src: url };
        }
        // Fallback: try as iframe (best-effort)
        return { type: "iframe", src: url };
    } catch {
        return null;
    }
}

export function VideoTrigger({ url, label = "Watch sample", testId, className = "" }) {
    const [open, setOpen] = useState(false);
    const disabled = !url;
    return (
        <>
            <button
                type="button"
                disabled={disabled}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setOpen(true); }}
                className={`inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase transition-colors ${disabled ? "text-[#8A6574] cursor-not-allowed" : "text-[#3D0C1F] hover:text-[#8F622C]"} ${className}`}
                data-testid={testId}
                title={disabled ? "Sample video coming soon" : undefined}
            >
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${disabled ? "bg-[#6B1535]/10 text-[#8A6574]" : "bg-[#6B1535] text-[#FFF8F2]"} transition-transform group-hover:scale-110`}>
                    <Play size={12} fill="currentColor" />
                </span>
                {disabled ? "Sample coming soon" : label}
            </button>
            {open && <VideoPlayerModal url={url} onClose={() => setOpen(false)} />}
        </>
    );
}

export function VideoPlayerModal({ url, onClose }) {
    const embed = toEmbedUrl(url);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [onClose]);

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[#3D0C1F]/80 backdrop-blur-sm px-4 py-12"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            data-testid="video-modal"
        >
            <button
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="absolute top-6 right-6 text-[#3D0C1F] hover:text-[#8F622C] transition-colors z-10"
                data-testid="video-close"
                aria-label="Close video"
            >
                <X size={28} />
            </button>
            <div
                className="relative w-full max-w-5xl aspect-video bg-[#FBF6F0] border border-[#C29A5B]/30 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                {!embed && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-[#7A5262]">
                        Unable to play this video URL.
                    </div>
                )}
                {embed?.type === "iframe" && (
                    <iframe
                        src={embed.src}
                        className="w-full h-full"
                        title="Video"
                        frameBorder="0"
                        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                        allowFullScreen
                    />
                )}
                {embed?.type === "video" && (
                    <video src={embed.src} controls autoPlay playsInline className="w-full h-full bg-black">
                        Your browser does not support video playback.
                    </video>
                )}
            </div>
        </div>
    );
}
